/**
 * Formatadores BR-specific + helpers de string.
 *
 * Convenções:
 *   - format*    → adiciona máscara (string → string formatada)
 *   - unformat*  → remove tudo não-dígito (string formatada → string crua)
 *   - is*Valid   → validação estrutural (não verifica dígitos verificadores)
 *
 * Pra moeda usar locale do user vindo do ThemeProvider:
 *   const { locale } = useTheme();
 *   formatCurrency(123456, locale)  → "R$ 1.234,56" / "$1,234.56" / "1.234,56 €"
 */
import type { LocaleCode } from "./preferences";


// ════════════════════════════════════════════════════════════════════════════
// CNPJ — 14 dígitos: 99.999.999/9999-99
// ════════════════════════════════════════════════════════════════════════════
export function formatCNPJ(value: string | null | undefined): string {
  if (!value) return "";
  const digits = value.replace(/\D/g, "").slice(0, 14);
  if (digits.length <= 2)  return digits;
  if (digits.length <= 5)  return `${digits.slice(0,2)}.${digits.slice(2)}`;
  if (digits.length <= 8)  return `${digits.slice(0,2)}.${digits.slice(2,5)}.${digits.slice(5)}`;
  if (digits.length <= 12) return `${digits.slice(0,2)}.${digits.slice(2,5)}.${digits.slice(5,8)}/${digits.slice(8)}`;
  return `${digits.slice(0,2)}.${digits.slice(2,5)}.${digits.slice(5,8)}/${digits.slice(8,12)}-${digits.slice(12,14)}`;
}

export function unformatCNPJ(value: string): string {
  return value.replace(/\D/g, "").slice(0, 14);
}

export function isCNPJValid(value: string): boolean {
  const cnpj = unformatCNPJ(value);
  if (cnpj.length !== 14) return false;
  if (/^(\d)\1{13}$/.test(cnpj)) return false;  // todos dígitos iguais
  // Validação dos dígitos verificadores
  const calc = (slice: string) => {
    let sum = 0, pos = slice.length - 7;
    for (let i = slice.length; i >= 1; i--) {
      sum += parseInt(slice.charAt(slice.length - i), 10) * pos--;
      if (pos < 2) pos = 9;
    }
    const r = sum % 11;
    return r < 2 ? 0 : 11 - r;
  };
  if (calc(cnpj.slice(0, 12)) !== parseInt(cnpj.charAt(12), 10)) return false;
  if (calc(cnpj.slice(0, 13)) !== parseInt(cnpj.charAt(13), 10)) return false;
  return true;
}


// ════════════════════════════════════════════════════════════════════════════
// CPF — 11 dígitos: 999.999.999-99
// ════════════════════════════════════════════════════════════════════════════
export function formatCPF(value: string | null | undefined): string {
  if (!value) return "";
  const digits = value.replace(/\D/g, "").slice(0, 11);
  if (digits.length <= 3)  return digits;
  if (digits.length <= 6)  return `${digits.slice(0,3)}.${digits.slice(3)}`;
  if (digits.length <= 9)  return `${digits.slice(0,3)}.${digits.slice(3,6)}.${digits.slice(6)}`;
  return `${digits.slice(0,3)}.${digits.slice(3,6)}.${digits.slice(6,9)}-${digits.slice(9,11)}`;
}

export function unformatCPF(value: string): string {
  return value.replace(/\D/g, "").slice(0, 11);
}

export function isCPFValid(value: string): boolean {
  const cpf = unformatCPF(value);
  if (cpf.length !== 11) return false;
  if (/^(\d)\1{10}$/.test(cpf)) return false;
  let sum = 0;
  for (let i = 0; i < 9; i++)  sum += parseInt(cpf.charAt(i), 10) * (10 - i);
  let rev = 11 - (sum % 11);
  if (rev >= 10) rev = 0;
  if (rev !== parseInt(cpf.charAt(9), 10)) return false;
  sum = 0;
  for (let i = 0; i < 10; i++) sum += parseInt(cpf.charAt(i), 10) * (11 - i);
  rev = 11 - (sum % 11);
  if (rev >= 10) rev = 0;
  return rev === parseInt(cpf.charAt(10), 10);
}


// ════════════════════════════════════════════════════════════════════════════
// Telefone BR — celular (11 dig) ou fixo (10 dig)
// ════════════════════════════════════════════════════════════════════════════
export function formatPhone(value: string | null | undefined): string {
  if (!value) return "";
  const digits = value.replace(/\D/g, "").slice(0, 11);
  if (digits.length <= 2)   return `(${digits}`;
  if (digits.length <= 6)   return `(${digits.slice(0,2)}) ${digits.slice(2)}`;
  if (digits.length <= 10)  return `(${digits.slice(0,2)}) ${digits.slice(2,6)}-${digits.slice(6)}`;
  return `(${digits.slice(0,2)}) ${digits.slice(2,7)}-${digits.slice(7,11)}`;
}

export function unformatPhone(value: string): string {
  return value.replace(/\D/g, "").slice(0, 11);
}


// ════════════════════════════════════════════════════════════════════════════
// Moeda — locale-aware
// Recebe valor em CENTAVOS (inteiro) pra evitar floating point bugs.
// formatCurrency(123456, "pt") → "R$ 1.234,56"
// formatCurrency(123456, "en") → "$1,234.56"
// formatCurrency(123456, "es") → "1.234,56 €"
// ════════════════════════════════════════════════════════════════════════════
const CURRENCY_BY_LOCALE: Record<LocaleCode, { code: string; locale: string }> = {
  pt: { code: "BRL", locale: "pt-BR" },
  en: { code: "USD", locale: "en-US" },
  es: { code: "EUR", locale: "es-ES" },
};

export function formatCurrency(cents: number, locale: LocaleCode = "pt", currencyOverride?: string): string {
  const { code, locale: bcp } = CURRENCY_BY_LOCALE[locale] ?? CURRENCY_BY_LOCALE.pt;
  return new Intl.NumberFormat(bcp, {
    style: "currency",
    currency: currencyOverride ?? code,
  }).format(cents / 100);
}

/** Para forçar BRL independente do locale do user (ex: relatório fiscal BR) */
export function formatBRL(cents: number): string {
  return new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(cents / 100);
}

/** Parse de string formatada → centavos. "R$ 1.234,56" → 123456 */
export function parseCurrencyToCents(value: string): number | null {
  const digits = value.replace(/[^\d,.-]/g, "");
  // Detecta se separador decimal é vírgula (BR/ES) ou ponto (US)
  const hasComma = digits.includes(",");
  const hasDot   = digits.includes(".");
  let normalized = digits;
  if (hasComma && hasDot) {
    // O último é o decimal
    const lastComma = digits.lastIndexOf(",");
    const lastDot   = digits.lastIndexOf(".");
    if (lastComma > lastDot) normalized = digits.replace(/\./g, "").replace(",", ".");
    else                     normalized = digits.replace(/,/g, "");
  } else if (hasComma) {
    normalized = digits.replace(",", ".");
  }
  const num = parseFloat(normalized);
  if (isNaN(num)) return null;
  return Math.round(num * 100);
}


// ════════════════════════════════════════════════════════════════════════════
// Strings
// ════════════════════════════════════════════════════════════════════════════
export function truncate(text: string | null | undefined, maxLen: number, suffix = "…"): string {
  if (!text) return "";
  if (text.length <= maxLen) return text;
  return text.slice(0, maxLen - suffix.length) + suffix;
}

/** Slug determinístico ASCII sem acentos: "São Paulo!" → "sao-paulo" */
export function slugify(text: string): string {
  return text
    .toLowerCase()
    .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
}

/** Iniciais pra avatar fallback: "Maria Silva" → "MS"; "João" → "J" */
export function initials(name: string | null | undefined, max = 2): string {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 1).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase().slice(0, max);
}

/** Hash determinístico → cor da paleta. Mesmo nome sempre → mesma cor. */
export function colorFromString(s: string): string {
  // Paleta segura em ambos os modos
  const PALETTE = [
    "#0ea5e9", // sky
    "#6366f1", // indigo
    "#8b5cf6", // violet
    "#ec4899", // pink
    "#f97316", // orange
    "#14b8a6", // teal
    "#22c55e", // green
    "#eab308", // yellow
    "#ef4444", // red
  ];
  let hash = 0;
  for (let i = 0; i < s.length; i++) {
    hash = s.charCodeAt(i) + ((hash << 5) - hash);
    hash |= 0;
  }
  return PALETTE[Math.abs(hash) % PALETTE.length];
}
